// client/src/pages/LgmProductNavigator.tsx
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  lazy,
  Suspense,
} from 'react';
import ProductsReview from './ProductsReview';
import ManufacturerReview from './ManufacturerReview';
import Grades from './Grades';
import TherapeuticAreas from './TherapeuticAreas';
import useAuth from 'auth/auth';
import DevTools from 'components/DevTools';
import { DebugLayout } from 'components/DebugLayout';
import ValidationJobIndicator from 'components/ValidationJobIndicator';
import { useABTest } from 'contexts/ABTestContext';
// import QuotesReview from './QuotesReview';
import { useDeleteAllProducts } from 'services/api/domains/products/hooks';
import AuditLogs from './AuditLogs';
import { useViewportSize } from 'shared/hooks/useViewportSize';
import { useViewBackStack } from 'navigation/useViewBackStack';
import { LGM_ORANGE } from 'theme/colors';
import {
  GRAY_100,
  GRAY_200,
  GRAY_400,
  GRAY_500,
  GRAY_800,
} from 'theme/grayScale';

// Lazy load heavy components to reduce initial bundle size
const DashboardMetrics = lazy(() => import('./DashboardMetrics'));
const ManufacturerMap = lazy(() => import('./ManufacturerMap'));
const ValidationJobsPage = lazy(() => import('./ValidationJobsPage'));
const ProductUploadBatchesPage = lazy(
  () => import('./ProductUploadBatchesPage')
);
const ProductUploadBatchPage = lazy(() => import('./ProductUploadBatchPage'));

// LGM Intelligence Hub — landing portal for the Explorer ecosystem
// (www.lgmexplorer.com). The Navigator's "home" glyph links here so the app
// sits seamlessly within the suite alongside Explorer, Architecture,
// Ontology, and the Data Catalog.
const INTEL_HUB_URL = 'https://www.lgmexplorer.com/lgm-intelligence-hub.html';

// Loading fallback component for lazy-loaded routes
const LoadingFallback: React.FC<{ message: string }> = ({ message }) => (
  <div style={loadingStyles.containerStyle}>
    <div style={loadingStyles.spinnerStyle} />
    <p style={loadingStyles.textStyle}>{message}</p>
  </div>
);

// ─── Last-view persistence ────────────────────────────────────────────────────
type View =
  | 'products'
  | 'manufacturer_master'
  | 'manufacturer_map'
  | 'grades'
  | 'therapeutic_areas'
  | 'quotes'
  | 'audit_logs'
  | 'dashboard_metrics'
  | 'validation_jobs'
  | 'product_upload_batches';

const LAST_VIEW_KEY = 'lgm-last-view';
const LAST_BATCH_ID_KEY = 'lgm-last-product-upload-batch-id';

const VALID_VIEWS = new Set<string>([
  'products',
  'manufacturer_master',
  'manufacturer_map',
  'grades',
  'therapeutic_areas',
  'quotes',
  'audit_logs',
  'dashboard_metrics',
  'validation_jobs',
  'product_upload_batches',
]);

// Views that require admin role
const ADMIN_ONLY_VIEWS = new Set<string>([
  'grades',
  'therapeutic_areas',
  'audit_logs',
  'validation_jobs',
  'product_upload_batches',
]);

function getSavedView(): View {
  try {
    const raw = localStorage.getItem(LAST_VIEW_KEY);
    if (raw !== null && VALID_VIEWS.has(raw)) return raw as View;
  } catch {
    // localStorage unavailable; fall through
  }
  return 'products';
}

// Only meaningful when the saved view is 'product_upload_batches' -- a
// hard refresh while viewing a specific batch's detail page otherwise
// dropped the user back to the batch list, since `view` was persisted but
// the open batch id was not (issue #349 manual QA finding).
function getSavedBatchId(view: View): number | null {
  if (view !== 'product_upload_batches') return null;
  try {
    const raw = localStorage.getItem(LAST_BATCH_ID_KEY);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

const VIEW_TITLES: Record<View, string> = {
  products: 'Products',
  manufacturer_master: 'Manufacturers',
  manufacturer_map: 'Facilities Map',
  grades: 'Product Types',
  therapeutic_areas: 'Therapeutic Areas',
  quotes: 'RFQs',
  audit_logs: 'Audit Logs',
  dashboard_metrics: 'Metrics',
  validation_jobs: 'Validation Jobs',
  product_upload_batches: 'Product Uploads',
};

function getViewTitle(view: View): string {
  return `${VIEW_TITLES[view]} - LGM Navigator`;
}

// Keep-alive tab rendering (Requirement 3 / Architecture Decision 7): once a
// tab has been visited, it stays mounted and is hidden via `display: none`
// instead of being unmounted, so its filter/sort/pagination/scroll state
// survives a switch away and back. This is scoped to this top-level tab
// conditional only -- modal-level conditional rendering within each page is
// untouched.
function hiddenUnless(isActive: boolean): React.CSSProperties {
  return isActive ? {} : { display: 'none' };
}

// Same keep-alive semantics as `hiddenUnless`, but the active branch also
// threads a definite height down through the flex chain instead of a bare
// `{}` (block, auto height). Pages whose own layout already assumes a
// height-bounded ancestor (ProductsReview/ManufacturerReview's
// pageContainerStyle: height '100%') need this so their internal grid
// scrolls in place instead of the whole tab -- search bar included --
// scrolling as one block inside mainContentStyle (issue #631 follow-up).
function hiddenUnlessFill(isActive: boolean): React.CSSProperties {
  return isActive
    ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }
    : { display: 'none' };
}
// ─────────────────────────────────────────────────────────────────────────────

// ─── Cross-view filter payload (manufacturer-product-linking, Task 2.1) ──────
// LgmProductNavigator's view state has always been a bare string enum with no
// payload -- this is the first mechanism for one view to carry filter context
// into another. `manufacturerName` is carried alongside the id purely for
// display -- the target grid's advanced-search summary shows it instead of
// the raw id (matching how a manually-picked async-select filter renders),
// per UX feedback on PR #563.
export interface PendingManufacturerFilter {
  manufacturerId: number;
  manufacturerName?: string;
}
// ─────────────────────────────────────────────────────────────────────────────

const LgmProductNavigator: React.FC = () => {
  const { view, setView, replaceView } = useViewBackStack<View>(
    getSavedView,
    getViewTitle
  );

  // Tracks every view visited this session so it can stay mounted (hidden,
  // not removed) once switched away from -- see `hiddenUnless` above.
  const visitedViewsRef = useRef<Set<View>>(new Set());
  visitedViewsRef.current.add(view);
  const isVisited = useCallback(
    (v: View) => visitedViewsRef.current.has(v),
    []
  );

  // Cross-view filter payload (Task 2.1) -- set by navigateToXFiltered,
  // consumed (and cleared) by the target view's grid on mount. Two
  // independent slots since a filter could in principle be pending in both
  // directions at once (e.g. rapid back-and-forth navigation).
  const [pendingProductsFilter, setPendingProductsFilter] =
    useState<PendingManufacturerFilter | null>(null);
  const [pendingManufacturerFilter, setPendingManufacturerFilter] =
    useState<PendingManufacturerFilter | null>(null);

  // Forces a full remount of ProductsReview/ManufacturerReview (via `key`,
  // below) on every "View Products"/"View Manufacturer" click. Keep-alive
  // tabs (above) mean these pages normally stay mounted across a plain tab
  // switch, so their mount-only filter-seeding lazy initializers never
  // re-run for one -- a real deep-link click needs the fresh mount those
  // initializers assume in order to actually apply the new manufacturer
  // filter, rather than leaving whatever filter the page already had.
  const [productsFilterNonce, setProductsFilterNonce] = useState(0);
  const [manufacturerFilterNonce, setManufacturerFilterNonce] = useState(0);

  // Tracks the open batch so the render block below can switch between the
  // list page and ProductUploadBatchPage (Seq 11's detail page). Restored
  // from localStorage on mount (mirrors `view`'s own getSavedView()) so a
  // hard refresh while viewing a specific batch's detail page stays there
  // instead of falling back to the batch list.
  const [selectedProductUploadBatchId, setSelectedProductUploadBatchId] =
    useState<number | null>(() => getSavedBatchId(getSavedView()));
  const openProductUploadBatch = useCallback(
    (batchId: number) => {
      setSelectedProductUploadBatchId(batchId);
      setView('product_upload_batches');
    },
    [setView]
  );

  const navigateToProductsFiltered = useCallback(
    (manufacturerId: number, manufacturerName?: string) => {
      setPendingProductsFilter({ manufacturerId, manufacturerName });
      setProductsFilterNonce(n => n + 1);
      setView('products');
    },
    [setView]
  );

  const navigateToManufacturerFiltered = useCallback(
    (manufacturerId: number, manufacturerName?: string) => {
      setPendingManufacturerFilter({ manufacturerId, manufacturerName });
      setManufacturerFilterNonce(n => n + 1);
      setView('manufacturer_master');
    },
    [setView]
  );

  const clearPendingProductsFilter = useCallback(() => {
    setPendingProductsFilter(null);
  }, []);

  // Deep-link-to-product-modal support for ProductsReview (Task 2.2's
  // remount-on-click convention). The upload batch detail page's "View
  // Product" action used to drive this (issue #349, Sub-task 11.3) but now
  // renders the product modal locally instead, without leaving the batch
  // page (Test 6.11 back-navigation fix) -- `pendingProductIdToOpen` stays
  // permanently null today, kept only because `ProductsReview` still
  // declares the prop for any future deep-link caller.
  const [pendingProductIdToOpen, setPendingProductIdToOpen] = useState<
    number | null
  >(null);
  const clearPendingProductIdToOpen = useCallback(() => {
    setPendingProductIdToOpen(null);
  }, []);

  const clearPendingManufacturerFilter = useCallback(() => {
    setPendingManufacturerFilter(null);
  }, []);

  const { instance, account, isAuthenticated, isAdmin, roles } = useAuth();
  const { getTestValue } = useABTest();
  const isValidationJobIndicatorEnabled =
    isAdmin && getTestValue('validate_products_devtool');

  const [showDevTools, setShowDevTools] = useState(false);
  const deleteAllProductsMutation = useDeleteAllProducts();

  // Restore last-viewed page after authentication, with role gating. Falls
  // back to 'products' if the saved view requires admin and the user isn't
  // one, OR if the saved view is 'validation_jobs' specifically and the
  // validate_products_devtool AB flag is off (Copilot review, PR #683) --
  // ADMIN_ONLY_VIEWS alone doesn't catch an admin whose flag got disabled
  // after they last saved 'validation_jobs' as their view.
  useEffect(() => {
    if (account) {
      const saved = getSavedView();
      const savedRequiresAdmin = ADMIN_ONLY_VIEWS.has(saved) && !isAdmin;
      const savedRequiresDevtoolFlag =
        saved === 'validation_jobs' && !isValidationJobIndicatorEnabled;
      replaceView(
        savedRequiresAdmin || savedRequiresDevtoolFlag ? 'products' : saved
      );
    }
  }, [
    instance,
    account,
    isAdmin,
    isValidationJobIndicatorEnabled,
    replaceView,
  ]);

  // Persist the active view to localStorage so it survives page refreshes
  // and Azure AD authentication redirects.
  useEffect(() => {
    try {
      localStorage.setItem(LAST_VIEW_KEY, view);
    } catch {
      // localStorage unavailable; silently skip
    }
  }, [view]);

  // Mirrors the effect above for the open batch id -- removes the key
  // entirely when back at the list (null) so a later refresh from the
  // list page doesn't resurrect a stale batch id.
  useEffect(() => {
    try {
      if (selectedProductUploadBatchId === null) {
        localStorage.removeItem(LAST_BATCH_ID_KEY);
      } else {
        localStorage.setItem(
          LAST_BATCH_ID_KEY,
          String(selectedProductUploadBatchId)
        );
      }
    } catch {
      // localStorage unavailable; silently skip
    }
  }, [selectedProductUploadBatchId]);

  useEffect(() => {
    console.log('🔎 Current view:', view, 'Roles:', roles);
  }, [view, roles]);

  const handleDeleteAll = useCallback(async () => {
    if (!window.confirm('⚠️ Delete ALL products? This cannot be undone.')) {
      return;
    }
    try {
      await deleteAllProductsMutation.mutate();
    } catch (err: unknown) {
      alert(`❌ Failed to delete all products: ${JSON.stringify(err)}`);
    }
  }, [deleteAllProductsMutation]);

  useEffect(() => {
    if (deleteAllProductsMutation.status === 'success') {
      alert('✅ All products deleted successfully');
    }
  }, [deleteAllProductsMutation.status]);

  const { heightClass } = useViewportSize();
  const isSmall = heightClass === 'sm';
  const headerPadding: React.CSSProperties = isSmall
    ? { paddingTop: '0.5rem', paddingBottom: '0.5rem' }
    : {};
  const logoSize: React.CSSProperties = isSmall ? { height: '52px' } : {};
  const mainPadding: React.CSSProperties = isSmall
    ? { paddingTop: '0.75rem', paddingBottom: '0.75rem' }
    : {};
  const activeNavStyle: React.CSSProperties = isSmall
    ? { ...styles.navButtonActiveStyle, fontSize: '0.875rem' }
    : styles.navButtonActiveStyle;

  return (
    <div style={styles.mainContainerStyle}>
      {/* Top Navigation Bar */}
      <div
        style={{ ...styles.headerContainerStyle, ...headerPadding }}
        data-layout-role="app-header">
        <div style={styles.headerContentStyle}>
          <img
            src="/LGMNavigator.png"
            alt="LGM Pharma"
            style={{ ...styles.logoStyle, ...logoSize }}
          />

          {/* Navigation Buttons */}
          <div style={styles.navContainerStyle}>
            {/* Intelligence Hub button — ties Navigator into the
                Explorer ecosystem (lgmexplorer.com) */}
            <a
              href={INTEL_HUB_URL}
              title="Return to LGM Intelligence Hub"
              aria-label="LGM Intelligence Hub — Explorer ecosystem home"
              style={styles.navHomeStyle}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(241,90,34,0.22)'; // LGM_ORANGE tint
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(241,90,34,0.12)'; // LGM_ORANGE tint
              }}
              onFocus={e => {
                e.currentTarget.style.background = 'rgba(241,90,34,0.22)'; // LGM_ORANGE tint
              }}
              onBlur={e => {
                e.currentTarget.style.background = 'rgba(241,90,34,0.12)'; // LGM_ORANGE tint
              }}>
              <svg
                viewBox="0 0 24 24"
                style={{
                  width: '13px',
                  height: '13px',
                  fill: 'currentColor',
                  flexShrink: 0,
                }}
                aria-hidden="true">
                <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
              </svg>
              Hub
            </a>
            <button
              onClick={() => setView('products')}
              style={
                view === 'products' ? activeNavStyle : styles.navButtonStyle
              }>
              Products
            </button>
            <button
              onClick={() => setView('manufacturer_master')}
              style={
                view === 'manufacturer_master'
                  ? activeNavStyle
                  : styles.navButtonStyle
              }>
              Manufacturers
            </button>
            {isAdmin && (
              <>
                <button
                  onClick={() => setView('grades')}
                  style={
                    view === 'grades' ? activeNavStyle : styles.navButtonStyle
                  }>
                  Product Types
                </button>
                <button
                  onClick={() => setView('therapeutic_areas')}
                  style={
                    view === 'therapeutic_areas'
                      ? activeNavStyle
                      : styles.navButtonStyle
                  }>
                  Therapeutic Areas
                </button>
              </>
            )}
            <button
              onClick={() => setView('manufacturer_map')}
              style={
                view === 'manufacturer_map'
                  ? activeNavStyle
                  : styles.navButtonStyle
              }>
              Facilities Map
            </button>
            {/* <button
              disabled
              style={styles.navButtonDisabledStyle}
              title="Disabled for now">
              RFQs
            </button> */}
            <button
              onClick={() => setView('dashboard_metrics')}
              style={
                view === 'dashboard_metrics'
                  ? activeNavStyle
                  : styles.navButtonStyle
              }>
              Metrics
            </button>
            {isAdmin && (
              <button
                onClick={() => setView('audit_logs')}
                style={
                  view === 'audit_logs' ? activeNavStyle : styles.navButtonStyle
                }>
                Audit Logs
              </button>
            )}
            {isAdmin && (
              <button
                onClick={() => setView('product_upload_batches')}
                style={
                  view === 'product_upload_batches'
                    ? activeNavStyle
                    : styles.navButtonStyle
                }>
                Product Uploads
              </button>
            )}
          </div>

          <div style={styles.buttonGroupStyle}>
            <ValidationJobIndicator
              enabled={isValidationJobIndicatorEnabled}
              onClick={() => setView('validation_jobs')}
            />
            <button
              onClick={() => setShowDevTools(prev => !prev)}
              style={styles.headerButtonStyle}>
              {showDevTools ? 'Hide Dev Tools' : 'Show Dev Tools'}
            </button>
            <button
              onClick={() => instance.logoutRedirect()}
              style={styles.headerButtonStyle}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Developer Tools Panel */}
      {showDevTools && (
        <DevTools
          deleteAllStatus={deleteAllProductsMutation}
          onDeleteAll={handleDeleteAll}
        />
      )}

      {/* Main Content */}
      <div style={{ ...styles.mainContentStyle, ...mainPadding }}>
        {!isAuthenticated ? (
          <p style={styles.waitingTextStyle}>🔐 Waiting for login...</p>
        ) : (
          <>
            {/* Products */}
            {isVisited('products') && (
              <div style={hiddenUnlessFill(view === 'products')}>
                <ProductsReview
                  key={productsFilterNonce}
                  deleteAllStatus={deleteAllProductsMutation}
                  initialManufacturerFilter={pendingProductsFilter}
                  onInitialFilterConsumed={clearPendingProductsFilter}
                  onViewManufacturer={navigateToManufacturerFiltered}
                  onUpload={
                    isAdmin
                      ? () => setView('product_upload_batches')
                      : undefined
                  }
                  initialProductIdToOpen={pendingProductIdToOpen}
                  onInitialProductIdConsumed={clearPendingProductIdToOpen}
                />
              </div>
            )}
            {/* Manufacturers */}
            {isVisited('manufacturer_master') && (
              <div style={hiddenUnlessFill(view === 'manufacturer_master')}>
                <ManufacturerReview
                  key={manufacturerFilterNonce}
                  initialManufacturerId={
                    pendingManufacturerFilter?.manufacturerId
                  }
                  initialManufacturerName={
                    pendingManufacturerFilter?.manufacturerName
                  }
                  onInitialFilterConsumed={clearPendingManufacturerFilter}
                  onViewProducts={navigateToProductsFiltered}
                />
              </div>
            )}
            {/* Grades */}
            {isVisited('grades') && isAdmin && (
              <div style={hiddenUnlessFill(view === 'grades')}>
                <Grades />
              </div>
            )}
            {/* Therapeutic Areas */}
            {isVisited('therapeutic_areas') && isAdmin && (
              <div style={hiddenUnlessFill(view === 'therapeutic_areas')}>
                <TherapeuticAreas />
              </div>
            )}
            {/* Facilities Map */}
            {isVisited('manufacturer_map') && (
              <div style={hiddenUnless(view === 'manufacturer_map')}>
                <Suspense
                  fallback={
                    <LoadingFallback message="Loading facilities..." />
                  }>
                  <ManufacturerMap />
                </Suspense>
              </div>
            )}
            {/* RFQs */}
            {/* {view === 'quotes' && <QuotesReview />} */}
            {/* Dashboard Metrics */}
            {isVisited('dashboard_metrics') && (
              <div style={hiddenUnless(view === 'dashboard_metrics')}>
                <Suspense
                  fallback={<LoadingFallback message="Loading metrics..." />}>
                  <DashboardMetrics />
                </Suspense>
              </div>
            )}
            {/* Audit Logs */}
            {isVisited('audit_logs') && (
              <div style={hiddenUnlessFill(view === 'audit_logs')}>
                <AuditLogs />
              </div>
            )}
            {/* Validation Jobs (issue #637) -- gated on admin + the
                validate_products_devtool AB flag together, matching the
                nav indicator's own gating (isAdmin alone let a flag-off
                admin still render this view, Copilot review PR #683). */}
            {isVisited('validation_jobs') &&
              isValidationJobIndicatorEnabled && (
                <div style={hiddenUnless(view === 'validation_jobs')}>
                  <Suspense
                    fallback={
                      <LoadingFallback message="Loading validation jobs..." />
                    }>
                    <ValidationJobsPage isActive={view === 'validation_jobs'} />
                  </Suspense>
                </div>
              )}
            {/* Product Upload Batches (issue #349, Seq/UPS-010 + UPS-011). */}
            {isVisited('product_upload_batches') && isAdmin && (
              <div style={hiddenUnlessFill(view === 'product_upload_batches')}>
                <Suspense
                  fallback={
                    <LoadingFallback message="Loading upload batches..." />
                  }>
                  {selectedProductUploadBatchId !== null ? (
                    <ProductUploadBatchPage
                      batchId={selectedProductUploadBatchId}
                      onBack={() => setSelectedProductUploadBatchId(null)}
                    />
                  ) : (
                    <ProductUploadBatchesPage
                      isActive={view === 'product_upload_batches'}
                      onOpenBatch={openProductUploadBatch}
                    />
                  )}
                </Suspense>
              </div>
            )}
          </>
        )}
      </div>
      <DebugLayout />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  mainContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    fontFamily: 'inherit', // font-lgm
  } as React.CSSProperties,
  headerContainerStyle: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    backgroundColor: 'white',
    borderBottom: `1px solid ${GRAY_200}`, // border-b
    paddingBottom: '1rem', // pb-4
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1rem', // py-4
  } as React.CSSProperties,
  headerContentStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '2rem',
  } as React.CSSProperties,
  logoStyle: {
    height: '75px',
    objectFit: 'contain',
  } as React.CSSProperties,
  buttonGroupStyle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem', // gap-3
  } as React.CSSProperties,
  headerButtonStyle: {
    backgroundColor: GRAY_100, // bg-gray-100
    color: GRAY_800, // text-gray-800
    padding: '0.25rem 0.75rem', // px-3 py-1
    borderRadius: '0.25rem', // rounded
    fontSize: '0.875rem', // text-sm
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  navContainerStyle: {
    display: 'flex',
    alignItems: 'center', // align the home glyph with the text nav items
    gap: '1.5rem', // space-x-6
    color: GRAY_500, // text-gray-500
    fontSize: '0.875rem', // text-sm
    flex: 1,
    justifyContent: 'flex-start',
  } as React.CSSProperties,
  navHomeStyle: {
    fontSize: '12px',
    fontWeight: 600,
    padding: '5px 10px',
    borderRadius: '6px',
    background: 'rgba(241,90,34,0.12)', // LGM_ORANGE tint
    color: LGM_ORANGE,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  } as React.CSSProperties,
  navButtonStyle: {
    color: GRAY_500, // text-gray-500
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  navButtonActiveStyle: {
    color: '#ea580c', // text-orange-600
    fontWeight: 'bold',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.25rem',
  } as React.CSSProperties,
  navButtonDisabledStyle: {
    color: GRAY_400, // text-gray-400
    cursor: 'not-allowed',
    background: 'none',
    border: 'none',
    fontSize: '0.875rem',
  } as React.CSSProperties,
  mainContentStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'auto',
    paddingLeft: '3rem', // px-12
    paddingRight: '3rem',
    paddingTop: '1.5rem', // py-6
    paddingBottom: '1.5rem',
  } as React.CSSProperties,
  waitingTextStyle: {
    color: GRAY_500, // text-gray-500
  } as React.CSSProperties,
};

const loadingStyles: Record<string, React.CSSProperties> = {
  containerStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    fontFamily: 'var(--font-lgm, sans-serif)',
  },
  spinnerStyle: {
    width: '48px',
    height: '48px',
    border: `4px solid ${GRAY_200}`,
    borderTop: `4px solid ${LGM_ORANGE}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  textStyle: {
    marginTop: '16px',
    color: GRAY_500,
    fontSize: '14px',
  },
};

export default LgmProductNavigator;
